/**
 * Sticky "Total + Checkout" bar for mobile (cart drawer and /cart page).
 *
 * The previous implementation was an inline <script> inside cart-summary.liquid.
 * The cart drawer is refreshed with morph.js, which parses new HTML via
 * DOMParser — parsed <script> tags never execute — so the bar's observer died
 * after any morph and the bar never appeared (same bug class fixed for
 * cart-recs.js on 2026-06-19). It also looked up `#checkout` by id, which on
 * /cart resolves to the drawer's hidden button instead of the page's.
 *
 * This module loads once from the header and recomputes on events, so it
 * survives every morph. Each bar is scoped to its own context (the enclosing
 * <dialog> for the drawer instance, the document for the /cart page instance).
 * The bar's button submits natively via form="cart-form"; only visibility is
 * managed here.
 */

const BAR_SELECTOR = '.cronk-sticky-checkout';
const BUTTON_SELECTOR = '.cart__checkout-button';
const VISIBLE_CLASS = 'cronk-sticky-checkout--visible';
const MOBILE_MAX_WIDTH = 749;

let frame = null;

function update() {
  frame = null;
  const isMobile = window.innerWidth <= MOBILE_MAX_WIDTH;

  for (const bar of document.querySelectorAll(BAR_SELECTOR)) {
    let show = false;

    if (isMobile) {
      const dialog = bar.closest('dialog');
      const contextActive = dialog ? dialog.open : true;
      const button = (dialog ?? document).querySelector(BUTTON_SELECTOR);

      if (contextActive && button) {
        const rect = button.getBoundingClientRect();
        const rendered = rect.width > 0 && rect.height > 0;
        const outOfView = rect.bottom <= 0 || rect.top >= window.innerHeight;
        show = rendered && outOfView;
      }
    }

    bar.classList.toggle(VISIBLE_CLASS, show);
  }
}

function schedule() {
  if (frame === null) frame = requestAnimationFrame(update);
}

// Delayed pass for state that settles after the event (dialog open/close
// animation, morphed-in content, drawer auto-open after add-to-cart).
function scheduleSoon() {
  schedule();
  setTimeout(schedule, 350);
}

// Capture-phase so scrolls inside the drawer's scroll container are seen too
// (scroll events don't bubble).
document.addEventListener('scroll', schedule, { capture: true, passive: true });
window.addEventListener('resize', schedule, { passive: true });

// Cart mutations (morphs) and drawer open/close paths.
document.addEventListener('cart:update', scheduleSoon);
document.addEventListener('cart:add', scheduleSoon);
document.addEventListener('click', scheduleSoon, { passive: true });
document.addEventListener('close', scheduleSoon, { capture: true });
document.addEventListener('keydown', scheduleSoon, { passive: true });

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleSoon);
} else {
  scheduleSoon();
}
